/**
 * AppHeader — shared persistent header for all pages.
 *
 * WI-43 redesign:
 *   - Wordmark: 18×18px orange/accent square mark + lowercase "openrosterscout" text.
 *     The old SVG checkmark-in-rounded-rect is retired.
 *   - Header uses frosted-glass background (via .ors-header CSS class).
 *   - Nav: "Browse all schools" · "How it works" · "★ Watchlist" (with count badge) · "Sign in"/"Sign out"
 *   - SchoolLookupInput: rendered ONLY when schoolName prop is provided (SchoolDetailPage context).
 *     It is absent from the homepage header per AC-15.
 *
 * WI-56 notification center:
 *   - Bell icon added between Watchlist and Sign in/out separator (desktop).
 *   - Bell added between Watchlist star and hamburger (mobile).
 *   - Bell renders only for authenticated users.
 *   - Desktop: dropdown popover (320px, max-h 440px).
 *   - Mobile: bottom sheet (60% viewport height).
 *   - Outside-click and Escape close the dropdown.
 *
 * Retained:
 *   schoolName     — optional center slot for SchoolDetailPage (desktop only)
 *   isAuthenticated — controls Watchlist badge visibility and Sign in/out label
 *   watchlistCount  — optional number of saved schools for badge display
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SchoolLookupInput } from './SchoolLookupInput';
import { NotificationBell } from './NotificationBell';
import { NotificationsDropdown } from './NotificationsDropdown';
import { NotificationsBottomSheet } from './NotificationsBottomSheet';
import { useNotifications } from '../hooks/useNotifications';

interface AppHeaderProps {
  /** Optional center slot — school name on SchoolDetailPage.
   *  When provided, SchoolLookupInput renders in the header (search context). */
  schoolName?: string;
  /** Controls the nav link set (Watchlist vs Sign in). */
  isAuthenticated: boolean;
  /** Number of watchlist items for badge display. Defaults to 0. */
  watchlistCount?: number;
}

/** New WI-43 wordmark: orange square mark + lowercase text. */
function OrsWordmark() {
  return (
    <>
      <span className="ors-mark" aria-hidden="true" />
      <span>openrosterscout</span>
    </>
  );
}

export function AppHeader({ schoolName, isAuthenticated, watchlistCount = 0 }: AppHeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  /** SchoolLookupInput shows only when schoolName is provided (SchoolDetailPage). */
  const showSearch = Boolean(schoolName);

  // ── Notification state ───────────────────────────────────────────────────────
  const {
    unreadCount,
    notifications,
    isLoading: notificationsLoading,
    isOpen: notificationsOpen,
    openDropdown,
    closeDropdown,
    navigateToNotification,
  } = useNotifications(isAuthenticated);

  // Wrapper that toggles the dropdown open/closed on bell click.
  const handleBellClick = () => {
    if (notificationsOpen) {
      closeDropdown();
    } else {
      openDropdown();
    }
  };

  // ── Outside-click to close desktop dropdown ─────────────────────────────────
  const bellContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!notificationsOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (
        bellContainerRef.current &&
        !bellContainerRef.current.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [notificationsOpen, closeDropdown]);

  // Detect mobile viewport for sheet vs dropdown choice.
  // Using a CSS-based approach: the mobile bottom sheet is always rendered on
  // viewport < 768px; the dropdown on >= 768px. We render both but only the
  // appropriate one is visible based on CSS (hidden md:block / md:hidden).
  // For JS-gated rendering, use window.innerWidth at click time — but since
  // the dropdown is positioned absolute, we simply render both variants and
  // let the responsive classes do the work.
  // Actually: we render the desktop dropdown only in the desktop nav (hidden on mobile)
  // and the mobile sheet only in the mobile strip context.

  return (
    <header className="ors-header" role="banner">
      {/* ── Mobile layout ─────────────────────────────────────────── */}
      <div className="md:hidden h-full flex items-center justify-between px-4">
        <Link
          to="/"
          className="ors-wordmark"
          aria-label="OpenRosterScout home"
        >
          <OrsWordmark />
        </Link>

        <div className="flex items-center gap-1">
          {/* Watchlist badge */}
          {isAuthenticated && watchlistCount > 0 && (
            <Link
              to="/watchlist"
              className="flex items-center gap-1 text-[13px] font-medium px-2"
              style={{ color: 'var(--ors-accent)' }}
              aria-label={`Watchlist, ${watchlistCount} saved`}
            >
              <span aria-hidden="true">★</span>
              <span
                className="inline-flex items-center justify-center text-white text-[11px] font-bold rounded-full"
                style={{ background: 'var(--ors-accent)', minWidth: 18, height: 18, padding: '0 5px' }}
              >
                {watchlistCount}
              </span>
            </Link>
          )}

          {/* Notification bell — mobile, between Watchlist star and hamburger */}
          {isAuthenticated && (
            <NotificationBell
              unreadCount={unreadCount}
              isOpen={notificationsOpen}
              onClick={handleBellClick}
              onClose={closeDropdown}
            />
          )}

          {/* Hamburger */}
          <button
            type="button"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="ors-mobile-nav"
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="w-11 h-11 flex items-center justify-center rounded focus:outline-none focus-visible:ring-2"
            style={{ color: 'var(--ors-ink-2)' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {mobileMenuOpen
                ? <path d="M18 6 6 18M6 6l12 12" />
                : <path d="M3 6h18M3 12h18M3 18h18" />}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile bottom sheet — visible only on mobile (md:hidden wrapper ensures
          it does not appear on desktop, where the inline dropdown is used instead) */}
      {isAuthenticated && (
        <div className="md:hidden">
          <NotificationsBottomSheet
            isOpen={notificationsOpen}
            notifications={notifications}
            isLoading={notificationsLoading}
            onClose={closeDropdown}
            onNotificationClick={(n) => {
              navigateToNotification(n);
              closeMobileMenu();
            }}
          />
        </div>
      )}

      {/* Mobile nav drawer */}
      {mobileMenuOpen && (
        <nav
          id="ors-mobile-nav"
          aria-label="Main navigation"
          className="md:hidden absolute left-0 right-0 z-50 border-b"
          style={{ top: 56, background: 'var(--ors-surface)', borderColor: 'var(--ors-line)' }}
        >
          <div className="px-4 py-2 flex flex-col gap-0">
            <Link
              to="/browse"
              onClick={closeMobileMenu}
              className="py-3 text-[14px] font-medium border-b"
              style={{ color: 'var(--ors-ink-2)', borderColor: 'var(--ors-line)' }}
            >
              Browse all schools
            </Link>
            <Link
              to="/camps"
              onClick={closeMobileMenu}
              className="py-3 text-[14px] font-medium border-b"
              style={{ color: 'var(--ors-ink-2)', borderColor: 'var(--ors-line)' }}
            >
              Camps
            </Link>
            <Link
              to="/"
              onClick={closeMobileMenu}
              className="py-3 text-[14px] font-medium border-b"
              style={{ color: 'var(--ors-ink-2)', borderColor: 'var(--ors-line)' }}
            >
              How it works
            </Link>
            <Link
              to="/watchlist"
              onClick={closeMobileMenu}
              className="py-3 text-[14px] font-medium border-b"
              style={{ color: 'var(--ors-ink-2)', borderColor: 'var(--ors-line)' }}
            >
              ★ Watchlist{watchlistCount > 0 ? ` (${watchlistCount})` : ''}
            </Link>
            {isAuthenticated ? (
              <Link
                to="/logout"
                onClick={closeMobileMenu}
                className="py-3 text-[14px] font-medium"
                style={{ color: 'var(--ors-ink)', borderLeft: '1px solid var(--ors-line)' }}
              >
                Sign out
              </Link>
            ) : (
              <Link
                to="/login"
                onClick={closeMobileMenu}
                className="py-3 text-[14px] font-semibold"
                style={{ color: 'var(--ors-ink)' }}
              >
                Sign in
              </Link>
            )}
          </div>
        </nav>
      )}

      {/* ── Desktop layout ─────────────────────────────────────────── */}
      <div className="ors-header-inner hidden md:flex">

        {/* Wordmark */}
        <Link
          to="/"
          className="ors-wordmark"
          aria-label="OpenRosterScout home"
        >
          <OrsWordmark />
        </Link>

        {/* School name center slot (SchoolDetailPage only) */}
        {schoolName && (
          <span className="hidden lg:block flex-1 text-sm font-medium truncate text-center max-w-xs" style={{ color: 'var(--ors-ink-2)' }}>
            {schoolName}
          </span>
        )}

        {!schoolName && <div className="flex-1" />}

        {/* Search field — only on SchoolDetailPage */}
        {showSearch && <SchoolLookupInput />}

        {/* Nav */}
        <nav
          className="flex items-center gap-6 text-[13.5px] font-medium"
          style={{ color: 'var(--ors-muted)' }}
          aria-label="Primary"
        >
          <Link to="/browse" className="hover:text-gray-900 transition-colors focus:outline-none focus-visible:underline" style={{ color: 'var(--ors-muted)' }}>
            Browse all schools
          </Link>
          <Link
            to="/camps"
            className="hover:text-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
            style={{ color: 'var(--ors-muted)' }}
          >
            Camps
          </Link>
          <Link to="/" className="hover:text-gray-900 transition-colors focus:outline-none focus-visible:underline" style={{ color: 'var(--ors-muted)' }}>
            How it works
          </Link>
          <Link
            to="/watchlist"
            className="flex items-center gap-1.5 transition-colors focus:outline-none focus-visible:underline"
            style={{ color: watchlistCount > 0 ? 'var(--ors-accent)' : 'var(--ors-muted)' }}
            aria-label={watchlistCount > 0 ? `Watchlist, ${watchlistCount} saved` : 'Watchlist'}
          >
            <span aria-hidden="true">★</span>
            <span>Watchlist</span>
            {watchlistCount > 0 && (
              <span
                className="inline-flex items-center justify-center text-white text-[11px] font-bold rounded-full"
                style={{ background: 'var(--ors-accent)', minWidth: 18, height: 18, padding: '0 5px' }}
              >
                {watchlistCount}
              </span>
            )}
          </Link>

          {/* Notification bell — desktop, between Watchlist and Sign in/out separator */}
          {isAuthenticated && (
            <div ref={bellContainerRef} className="relative">
              <NotificationBell
                unreadCount={unreadCount}
                isOpen={notificationsOpen}
                onClick={handleBellClick}
                onClose={closeDropdown}
              />

              {/* Desktop dropdown */}
              {notificationsOpen && (
                <NotificationsDropdown
                  notifications={notifications}
                  isLoading={notificationsLoading}
                  onClose={closeDropdown}
                  onNotificationClick={navigateToNotification}
                />
              )}
            </div>
          )}

          {isAuthenticated ? (
            <Link
              to="/logout"
              className="pl-6 font-semibold transition-colors focus:outline-none focus-visible:underline"
              style={{ color: 'var(--ors-ink)', borderLeft: '1px solid var(--ors-line)' }}
            >
              Sign out
            </Link>
          ) : (
            <Link
              to="/login"
              className="pl-6 font-semibold transition-colors focus:outline-none focus-visible:underline"
              style={{ color: 'var(--ors-ink)', borderLeft: '1px solid var(--ors-line)' }}
            >
              Sign in
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
